import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader2, Armchair, MessageSquareText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeatFinder() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/seat-finder/${slug}`).then((r) => setMeta(r.data)).catch(() => setError('This seat finder is not available.'));
  }, [slug]);

  useEffect(() => {
    if (q.trim().length < 3) { setMatches(null); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { setMatches((await axios.get(`${API}/seat-finder/${slug}/search`, { params: { q } })).data.matches); } catch { setMatches([]); }
      setBusy(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, slug]);

  return (
    <div className="min-h-screen bg-[#FBF7F2] px-5 py-12 flex flex-col items-center" data-testid="seat-finder-page">
      <div className="w-full max-w-md">
        <p className="text-xs uppercase tracking-[0.25em] text-[#8A6A32] mb-3">{meta?.couple || 'Welcome'}</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#7B2C3B] mb-8 italic">Find your seat</h1>
        {error ? <p data-testid="seat-finder-error" className="text-[#6A6A6A]">{error}</p> : (
          <>
            <label className="relative block">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#B8896C]" />
              <input data-testid="seat-finder-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type your first or last name"
                className="w-full rounded-2xl border border-[#EFE3D8] bg-white pl-12 pr-4 py-4 text-lg focus:outline-none focus:border-[#B8896C] focus:ring-2 focus:ring-[#F4E4DA] transition-colors" />
            </label>
            <div className="mt-6 space-y-3" data-testid="seat-finder-results">
              {busy && <Loader2 className="w-5 h-5 animate-spin text-[#B8896C]" />}
              {!busy && matches?.length === 0 && <p data-testid="seat-finder-empty" className="text-sm text-[#6A6A6A]">No match yet. Try your full first and last name, or ask a member of the wedding party.</p>}
              {!busy && matches?.map((m, i) => (
                <div key={`${m.name}-${i}`} data-testid={`seat-finder-match-${i}`} className="bg-white rounded-2xl border border-[#EFE3D8] p-4 flex items-center gap-4 animate-float-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <span className="w-11 h-11 rounded-xl bg-[#F4E4DA] text-[#7B2C3B] flex items-center justify-center"><Armchair className="w-5 h-5" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg text-[#2A2A2A] truncate">{m.name}</p>
                    <p className="text-sm text-[#8A6A32]">{m.table}</p>
                  </div>
                </div>
              ))}
            </div>
            {meta?.text_number && (
              <p className="mt-10 text-sm text-[#6A6A6A] flex items-center gap-2" data-testid="seat-finder-text-tip">
                <MessageSquareText className="w-4 h-4 text-[#B8896C]" /> Or text <strong>SEAT</strong> and your name to {meta.text_number}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
